import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { modalMinWidth } from '@env/config';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { TechnicalPoint } from '@app/tech-points/_models/technical-point.model';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { prepareDateForDb, prepareDateForForm } from '@app/_utils/date-util';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';
import { ListIcashComparison } from '../_models/list-icash-comparison.model';
import { IcashStore } from '../_models/icash-store.model';
import { FiscalReceiptsService } from '../_services/fiscal-receipts.service';
import { IcashComparisonService } from '../_services/icash-comparison.service';
import { IcashComparisonDetailDialogComponent } from './icash-comparison-detail-dialog/icash-comparison-detail-dialog.component';

/**
 * The iCash comparison report - finished inspections checked against the receipts iCash issued.
 *
 * The screen joins two systems that share no key. iCash knows stores, we know technical points,
 * and the only thing the two have in common is the name - so picking an Обект has to resolve both
 * sides at once: the storeId the receipts are read by, and the mobileAppId the inspections are.
 * Keeping that pair in step is what makes the comparison meaningful, which is why the store list
 * is built from the stores that could be matched to a point rather than from iCash's own list.
 */
@Component({
  selector: 'app-icash-comparison',
  templateUrl: './icash-comparison.component.html'
})
export class IcashComparisonComponent extends BaseSortableSearchComponent<ListIcashComparison> implements OnInit, OnDestroy {

  // Units
  stores: IcashStore[] = [];
  /** storeId -> the technical point it was matched to, so the filter can send both. */
  private pointsByStoreId = new Map<number, TechnicalPoint>();

  // Payload
  dateObj = new Date();
  /** The points this user is tied to, from the token. Empty for an unrestricted user. */
  userTechnicalPointMobileAppIds: number[];

  // Summary tiles, counted over the whole period rather than the page
  totalInspections = 0;
  totalSales = 0;
  matchedCount = 0;
  differentCount = 0;
  missingReceiptCount = 0;
  // Split by type, so a problem sitting in sales or in inspections is not averaged away.
  inspectionMatchedCount = 0;
  inspectionDifferentCount = 0;
  inspectionMissingReceiptCount = 0;
  saleMatchedCount = 0;
  saleDifferentCount = 0;
  saleMissingReceiptCount = 0;
  totalDifference = 0;
  currencyCode = '';

  // Booleans
  /** True once the Обект list has been narrowed to the user's own point(s). */
  hasTechnicalPoint = false;

  // Form
  searchForm = this.formBuilder.group({
    storeId:              [''],
    techPointMobileAppId: [''],
    status:               ['ALL'],
    // Empty shows both kinds, which is the useful default - a receipt belongs to one or the other
    // and the point of the screen is seeing them against one another.
    subjectType:          [''],
    dateFrom:             prepareDateForForm(this.dateObj),
    dateTo:               prepareDateForForm(this.dateObj),
  });

  // Observables
  searchSubscription: Subscription;
  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() => {
      return this.icashComparisonService.findAllByFilter(this.sortingPaging, this.searchForm.value,
                                                        prepareDateForDb(this.searchForm.get('dateFrom')?.value),
                                                        prepareDateForDb(this.searchForm.get('dateTo')?.value)).pipe(
        tap(page => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = page.totalElements;
          this.content = page.content;

          this.totalInspections = page.totalInspections;
          this.totalSales = page.totalSales ?? 0;
          this.matchedCount = page.matchedCount;
          this.differentCount = page.differentCount;
          this.missingReceiptCount = page.missingReceiptCount;
          this.inspectionMatchedCount = page.inspectionMatchedCount ?? 0;
          this.inspectionDifferentCount = page.inspectionDifferentCount ?? 0;
          this.inspectionMissingReceiptCount = page.inspectionMissingReceiptCount ?? 0;
          this.saleMatchedCount = page.saleMatchedCount ?? 0;
          this.saleDifferentCount = page.saleDifferentCount ?? 0;
          this.saleMissingReceiptCount = page.saleMissingReceiptCount ?? 0;
          this.totalDifference = page.totalDifference ?? 0;
          this.currencyCode = page.content?.find(row => row.currencyCode)?.currencyCode ?? '';

          this.dataLoaded = true;
        })
      )
    }),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      // Prefer what the backend said - a period longer than 31 days has its own message, and the
      // generic one would leave the operator with nothing to act on.
      this.errorMessageSubject.next(this.resolveErrorMessage(err));
      return EMPTY;
    })
  );

  constructor(
    public perms: PermissionsService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private technicalPointService: TechnicalPointService,
    private fiscalReceiptsService: FiscalReceiptsService,
    private icashComparisonService: IcashComparisonService
    ) {
      super();
      this.userTechnicalPointMobileAppIds = AuthenticationService.getEmployeeTechPointsIds();
  }

  async ngOnInit() {
    await this.loadStores();
    this.searchSubscription = this.search$.subscribe();
    this.runSearch();
  }

  /**
   * Searches, unless there is no store with a technical point behind it to search.
   *
   * Without one there is nothing to compare the receipts against, so every receipt would be
   * reported as an inspection that never happened. Saying so is the honest answer; running the
   * search and showing that is not. Covers both the store picked having no point and a КТП
   * employee having no usable store at all.
   */
  private runSearch() {
    if (this.nothingToReport) {
      this.content = [];
      this.totalInspections = this.totalSales = 0;
      this.matchedCount = this.differentCount = this.missingReceiptCount = 0;
      this.inspectionMatchedCount = this.inspectionDifferentCount = this.inspectionMissingReceiptCount = 0;
      this.saleMatchedCount = this.saleDifferentCount = this.saleMissingReceiptCount = 0;
      this.totalDifference = 0;
      this.sortingPaging.totalElements = 0;
      this.sortingPaging.fromRow = 0;
      this.sortingPaging.toRow = 0;
      this.dataLoaded = true;
      return;
    }

    this.searchSubject.next();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  /**
   * Builds the Обект dropdown.
   *
   * A КТП employee sees only the store behind their own point. Their token carries the points they
   * work at, so listing the rest of the estate would offer them two dozen objects they cannot
   * report on - the same narrowing the other restricted screens apply.
   *
   * For everyone else every store iCash knows is listed, linked or not. A store whose technical
   * point has no iCash обект ID set cannot be reported on, but hiding it would make it vanish from
   * the filter with nothing to say why. Those are shown, marked, and refused at selection time
   * instead, which is the difference between a screen that looks broken and one that explains
   * itself.
   */
  private async loadStores() {
    try {
      const [stores, technicalPoints] = await Promise.all([
        this.fiscalReceiptsService.findStores().toPromise(),
        this.loadTechnicalPoints()
      ]);

      this.pointsByStoreId = this.indexPointsByStore(technicalPoints);
      this.hasTechnicalPoint = this.userTechnicalPointMobileAppIds.length > 0;

      // loadTechnicalPoints already returned only this user's points, so the index holds only
      // their stores - keeping the ones it knows about is the whole narrowing.
      this.stores = this.hasTechnicalPoint
        ? stores.filter(store => this.pointsByStoreId.has(store.storeId))
        : stores;

      if (this.hasTechnicalPoint && this.stores.length === 0) {
        // Their point exists but carries no iCash обект ID, so there is nothing to report on.
        // Falling back to every store would silently widen a restricted user's view, so the
        // dropdown is left empty and the screen says why.
        console.warn('No iCash store is linked to the technical points of this user',
          technicalPoints?.map(point => point.shortName || point.name));
        return;
      }

      const unmatched = this.stores.filter(store => !this.pointsByStoreId.has(store.storeId));
      if (unmatched.length > 0) {
        // Worth a line even when most stores are linked: a report that silently covers half the
        // estate is worse than one that says which half it cannot.
        console.warn(`${unmatched.length} of ${this.stores.length} iCash stores have no technical point linked to them`,
          unmatched.map(store => store.name));
      }

      // Open on a store the report can actually run for, so the screen lands on something useful.
      const firstUsable = this.stores.find(store => this.pointsByStoreId.has(store.storeId));
      if (firstUsable) {
        this.selectStore(firstUsable.storeId);
      }
    } catch (err) {
      displayErrorFromUnknown(err);
    }
  }

  /** The user's own points when they are tied to any, otherwise every point. */
  private async loadTechnicalPoints(): Promise<TechnicalPoint[]> {
    if (this.userTechnicalPointMobileAppIds.length === 0) {
      return this.technicalPointService.findTechnicalPoints().toPromise();
    }
    return this.technicalPointService.findTechnicalPointsByIds(this.userTechnicalPointMobileAppIds).toPromise();
  }

  /**
   * Indexes the technical points by the iCash store each one is linked to.
   *
   * The link is now recorded on the point (iCash обект ID on its edit screen) rather than guessed
   * from the name, so this is a straight lookup. Points with no store set are skipped - they are
   * simply not configured, which is what leaves their store marked in the dropdown.
   */
  private indexPointsByStore(technicalPoints: TechnicalPoint[]): Map<number, TechnicalPoint> {
    const byStore = new Map<number, TechnicalPoint>();

    (technicalPoints || []).forEach(point => {
      if (point.icashStoreId != null && point.mobileAppId) {
        byStore.set(point.icashStoreId, point);
      }
    });

    return byStore;
  }

  /** Keeps the two sides of the filter in step - the iCash store and the point behind it. */
  private selectStore(storeId: number) {
    this.searchForm.get('storeId')?.patchValue(storeId);
    this.searchForm.get('techPointMobileAppId')?.patchValue(this.pointsByStoreId.get(+storeId)?.mobileAppId ?? '');
  }

  /** True when this store has no technical point behind it, so the report cannot be run for it. */
  isUnmatchedStore(storeId: number): boolean {
    return !this.pointsByStoreId.has(+storeId);
  }

  /** The store currently picked has no counterpart on our side. */
  get selectedStoreUnmatched(): boolean {
    const storeId = this.searchForm.get('storeId')?.value;
    return !!storeId && this.isUnmatchedStore(storeId);
  }

  /**
   * A КТП employee with nothing to report on - their point carries no iCash обект ID, so the
   * narrowed dropdown came out empty and there is no store to fall back to.
   */
  get noStoreAvailable(): boolean {
    return this.hasTechnicalPoint && this.stores.length === 0;
  }

  /** Either way there is nothing to compare, so the summary and the table stay out of the way. */
  get nothingToReport(): boolean {
    return this.selectedStoreUnmatched || this.noStoreAvailable;
  }

  onStoreChange() {
    this.selectStore(this.searchForm.get('storeId')?.value);
  }

  search() {
    this.sortingPaging.pageNumber = 1;
    this.runSearch();
  }

  clearSearch() {
    this.searchForm = this.formBuilder.group({
      storeId:              [''],
      techPointMobileAppId: [''],
      status:               ['ALL'],
      subjectType:          [''],
      dateFrom:             prepareDateForForm(this.dateObj),
      dateTo:               prepareDateForForm(this.dateObj),
    });

    // The report is meaningless without a store, and a КТП employee must not be handed an empty
    // one that would widen their search - so clearing returns to the first store the report can
    // actually run for, skipping any that have no technical point behind them.
    const firstUsable = this.stores.find(store => !this.isUnmatchedStore(store.storeId));
    if (firstUsable) {
      this.selectStore(firstUsable.storeId);
    }

    this.sortingPaging.pageNumber = 1;
    this.runSearch();
  }

  /** Colours the status so the rows needing work stand out of the page. */
  matchStatusClass(matchStatus: string): string {
    switch (matchStatus) {
      case 'MATCHED':         return 'badge badge-success';
      case 'DIFFERENT':       return 'badge badge-warning';
      case 'MISSING_RECEIPT': return 'badge badge-danger';
      default:                return 'badge badge-light';
    }
  }

  matchStatusKey(matchStatus: string): string {
    return matchStatus ? 'icashComparison.status.' + matchStatus : '';
  }

  /**
   * How the row was paired - by the number the operator typed, or inferred from the clock.
   *
   * Worth stating plainly on the row: a pairing made on the receipt number is a fact, while one
   * made on time is an inference that can go wrong when the till is busy. Blank on a row with no
   * receipt, where there was nothing to pair.
   */
  pairingMethodKey(pairingMethod?: string): string {
    return pairingMethod ? 'icashComparison.pairing.' + pairingMethod : '';
  }

  /**
   * Opens the line-by-line comparison.
   *
   * Guarded as well as disabled in the template: without a receipt there is no operationId to
   * fetch, and the call would fail whatever route reached it.
   */
  viewComparison(row: ListIcashComparison) {
    // A row carries one id or the other, never both; without one there is nothing to open.
    const subjectId = row.subjectType === 'SALE' ? row.saleId : row.technicalInspectionId;

    if (!row.receiptOperationId || !subjectId || this.dialog.openDialogs.length > 0) {
      return;
    }

    this.dialog.open(IcashComparisonDetailDialogComponent, {
      ...modalMinWidth,
      data: {
        technicalInspectionId: subjectId,
        subjectType: row.subjectType,
        operationId: row.receiptOperationId,
        registrationNumber: row.registrationNumber
      }
    });
  }

  /** Labels the row as an inspection or a sale. */
  subjectTypeKey(subjectType?: string): string {
    return subjectType ? 'icashComparison.type.' + subjectType : '';
  }

  /** The backend's DAP code if it sent one, otherwise the generic loading message. */
  private resolveErrorMessage(err: any): string {
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) : null;

    // instant() echoes the key back when there is no translation for it.
    if (translated && translated !== i18nKey) {
      return translated;
    }

    return this.translateService.instant('messages.errorLoadingData');
  }

}
